import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAccount, useDisconnect } from "wagmi";
import { ethers } from "ethers";
import {
  getValidatedChainId,
  isTrustWallet,
  handleTrustWalletChainIdMismatch,
} from "@/utils/chain-id-helpers";
import { useWallet } from "@/hooks/use-wallet";
import {
  createUserProfile,
  getProfile,
  updateUserProfile,
} from "@/services/auth.service";
import axiosInstance, { deleteAuthToken, setAuthToken } from "@/utils/axios";
import { NFTRequiredError } from "@/utils/nft-error";
import FullPageLoader from "@/components/loaders/full-page-loader";
import { authLogin, checkAddress } from "@/services/auth.service";
import { getBalance } from "@/features/user/services/user.service";

const initialAuthState: AuthState = {
  isLoading: true,
  isAuthenticated: false,
  user: null,
  tokens: 0,
};

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, setState] = useState<AuthState>(initialAuthState);
  const wallet = useWallet();
  const { disconnect } = useDisconnect();
  const { isConnecting, isReconnecting, status } = useAccount();
  const isUpdatingTokensRef = useRef(false);
  const lastUpdateTokensTimeRef = useRef(0);

  const login = useCallback(
    async ({ address, chainId }: LoginParams) => {
      try {
        if (!address || !ethers.isAddress(address)) {
          throw new Error("Invalid wallet address provided.");
        }
        let signer: ethers.Signer | null = null;
        if (wallet.provider) {
          try {
            signer = await wallet.provider.getSigner(address);
          } catch (signerError) {
            console.warn("Could not get signer from provider:", signerError);
            try {
              signer = await wallet.provider.getSigner();
            } catch {
              throw new Error(
                "Cannot get signer - provider may not support signing",
              );
            }
          }
        }

        if (!signer) {
          throw new Error("Wallet provider or signer is not available.");
        }

        const validatedChainId = await getValidatedChainId(
          wallet.provider,
          chainId || 1,
        );
        const isTrustWalletDetected = isTrustWallet();
        if (isTrustWalletDetected) {
          console.log(
            "Trust Wallet detected, using enhanced compatibility mode",
          );
        }
        const currentOrigin = window.location.origin;
        const dynamicDomain = currentOrigin;

        const checkAddressParams = {
          address,
          chainId: validatedChainId,
          domain: dynamicDomain,
        };

        const checkAddressResponse = await checkAddress(checkAddressParams);
        const message: string = (checkAddressResponse as any).message;

        let signature: string;
        let finalChainId = validatedChainId;
        try {
          signature = await signer.signMessage(message);
        } catch (signError: any) {
          if (
            signError.message &&
            signError.message.includes("ChainId mismatch")
          ) {
            console.warn(
              "Trust Wallet chainId mismatch detected, using utility handler",
            );
            try {
              const { response: retryResponse } =
                await handleTrustWalletChainIdMismatch(
                  wallet.provider as ethers.Provider,
                  address,
                  axiosInstance,
                  signer as ethers.Signer,
                );

              setAuthToken(retryResponse.data.token);
              setState((prev) => ({
                ...prev,
                isLoading: false,
                isAuthenticated: true,
                token: retryResponse.data.token,
                user: {
                  ...retryResponse.data.user,
                  isProfileCreated:
                    retryResponse.data.user.isProfileCreated ?? false,
                },
              }));
              return;
            } catch (retryError) {
              console.error(
                "Trust Wallet chainId mismatch handling failed:",
                retryError,
              );
              throw signError;
            }
          } else {
            throw signError;
          }
        }

        const loginPayload = {
          message,
          signature,
          chainId: finalChainId,
          from: "rewards",
        };

        const loginResponse = await authLogin(loginPayload);
        setAuthToken(loginResponse.token);

        setState((prev) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: true,
          token: (loginResponse as any).token,
          user: (loginResponse as any).user,
        }));
      } catch (err: any) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));

        if (err.response?.status === 400) {
          throw new NFTRequiredError("Nft is required");
        }

        throw err;
      }
    },
    [wallet.provider],
  );

  const logout = useCallback(() => {
    deleteAuthToken();
    localStorage.removeItem("multiplier");
    disconnect();

    setState((prev) => ({
      ...prev,
      isAuthenticated: false,
      user: null,
      balanceData: null,
      token: undefined,
    }));
  }, [disconnect]);

  useEffect(() => {
    // Only logout if auth token explicitly exists BUT the user has legitimately un-hooked the wallet plugin 
    // Not during initial page load when Wagmi is still asynchronously mapping the local injected providers
    if (state.isAuthenticated && !wallet.address && !isConnecting && !isReconnecting && status === "disconnected") {
      logout();
    }
  }, [wallet.address, state.isAuthenticated, logout, isConnecting, isReconnecting, status]);

  const updateProfile = useCallback(async (data: Partial<User>) => {
    try {
      const updatedUser = await updateUserProfile(data);
      setState((prev: any) => ({
        ...prev,
        user: updatedUser,
      }));
      return updatedUser;
    } catch (error) {
      console.error("Error updating profile:", error);
      setState((prev) => ({
        ...prev,
        user: prev.user
          ? {
            ...prev.user,
            ...data,
          }
          : null,
      }));

      throw error;
    }
  }, []);

  const updateTokens = useCallback(async () => {
    if (!wallet.address) return;
    if (isUpdatingTokensRef.current) {
      console.warn("Token update already in progress, skipping duplicate call");
      return;
    }
    const now = Date.now();
    if (now - lastUpdateTokensTimeRef.current < 2000) {
      console.warn("Token update called too soon, throttling duplicate call");
      return;
    }

    lastUpdateTokensTimeRef.current = now;
    isUpdatingTokensRef.current = true;
    try {
      const balance: any = await getBalance(wallet.address);
      if (balance.status) {
        setState((prev) => ({
          ...prev,
          tokens: balance.data,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          tokens: balance,
        }));
      }
    } catch (error) {
      console.error("Error updating tokens:", error);
    } finally {
      isUpdatingTokensRef.current = false;
    }
  }, []);

  const createProfile = useCallback(async (profileData: Partial<User>) => {
    try {
      const newUser = await createUserProfile(profileData);
      setState((prev) => ({
        ...prev,
        user: newUser,
      }));
      return newUser;
    } catch (error) {
      console.error("Error creating profile:", error);
      throw error;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!state.isAuthenticated) return;
    try {
      const profileData = await getProfile();

      setState((prev: any) => ({
        ...prev,
        user: profileData,
      }));

      return profileData;
    } catch (error) {
      console.error("Error refreshing profile:", error);
      throw error;
    }
  }, [state.isAuthenticated]);

  useEffect(() => {
    if (state.isAuthenticated && wallet.address) {
      updateTokens();
    }
  }, [wallet.address, state.isAuthenticated]);

  useEffect(() => {
    const init = async () => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const accessToken = localStorage.getItem("token");
      if (!accessToken) {
        return setState((prev) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: false,
        }));
      }
      setAuthToken(accessToken);
      try {
        const profileData = await getProfile();
        setState((prev: any) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: true,
          token: accessToken,
          user: profileData,
        }));
      } catch (err: any) {
        console.error(err);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: false,
          user: null,
          token: undefined,
        }));
        if (err.response?.status === 401) {
          deleteAuthToken();
        }
      }
    };

    init();
  }, []);

  const isProfileCreated = useMemo(() => {
    return state.user?.isProfileCreated ?? false;
  }, [state.user?.isProfileCreated]);

  const value = useMemo<AuthContextType>(
    () => ({
      isLoading: state.isLoading,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      tokens: state.tokens,
      isProfileCreated,
      login,
      logout,
      updateProfile,
      updateTokens,
      createProfile,
      refreshProfile,
    }),
    [
      state.isLoading,
      state.isAuthenticated,
      state.user,
      state.tokens,
      isProfileCreated,
      login,
      logout,
      updateProfile,
      updateTokens,
      createProfile,
      refreshProfile,
    ],
  );

  if (state.isLoading) {
    return <FullPageLoader />;
  }

  return <AuthContext value={value}>{children}</AuthContext>;
};
